<template>
  <StackPageLayout @close="navigateToMoreMenu">
    <template #title> Advanced Settings </template>

    <div class="space-y-6">
      <MoreMenuSection
        v-if="isCardholderNotPresentToggleable"
      >
        <MoreMenuSectionTitle>
          Payments
        </MoreMenuSectionTitle>
        <MoreMenuSubsection>
          <MoreMenuItemToggle
            v-model="cardholderNotPresentEnabled"
            :icon="IconPhone"
            label="Cardholder not present"
            description="Enable CNP transactions"
          />
        </MoreMenuSubsection>
      </MoreMenuSection>
      <MoreMenuSection>
        <MoreMenuSectionTitle>
          Receipts
        </MoreMenuSectionTitle>
        <MoreMenuSubsection>
          <MoreMenuItemToggle
            :model-value="isTransactionReceiptsEnabled"
            :icon="IconTransactionReceipts"
            label="Transaction Receipts"
            description="Enable transaction receipts"
            supervisor-required
            @update:model-value="
              handleToggleTransactionReceipts
            "
          />
          <MoreMenuItemToggle
            v-if="
              isCardReceiptsToggleable && printingSupported
            "
            :model-value="isCardReceiptsEnabled"
            :icon="IconCardReceipts"
            label="Card Receipts"
            description="Enable card receipts"
            supervisor-required
            @update:model-value="handleToggleCardReceipts"
          />
          <MoreMenuItemToggle
            :model-value="
              tenant.isAutomaticMerchantReceiptEnabled
            "
            :icon="IconCardReceipts"
            label="Auto Merchant Receipts"
            description="Automatically print out merchant copies of receipts"
            supervisor-required
            @update:model-value="
              (value) =>
                updateSetting(
                  'isAutomaticMerchantReceiptEnabled',
                  value
                )
            "
          />
          <MoreMenuItemToggle
            :model-value="isReceiptQrCodeEnabled"
            :icon="IconQRCode"
            label="QR Code Receipts"
            description="Print QR Code on receipts"
            supervisor-required
            @update:model-value="
              handleToggleQRCodesReceipts
            "
          />
        </MoreMenuSubsection>
      </MoreMenuSection>
      <MoreMenuSection>
        <MoreMenuSectionTitle>
          Access Control
        </MoreMenuSectionTitle>
        <MoreMenuSubsection>
          <MoreMenuItemToggle
            :model-value="
              accessControlRestrict.transactions
            "
            :icon="IconHistory"
            label="Transaction History"
            description="Require supervisor pin"
            supervisor-required
            @update:model-value="
              (event) =>
                handleToggleAccessControl(
                  'transactions',
                  event
                )
            "
          />
          <MoreMenuItemToggle
            :model-value="accessControlRestrict.reports"
            :icon="IconReports"
            label="Reports"
            description="Require supervisor pin"
            supervisor-required
            @update:model-value="
              (event) =>
                handleToggleAccessControl('reports', event)
            "
          />
        </MoreMenuSubsection>
      </MoreMenuSection>

      <MoreMenuSection v-if="isDarkModeAvailable">
        <MoreMenuSectionTitle>Theme</MoreMenuSectionTitle>
        <MoreMenuSubsection>
          <MoreMenuItemToggle
            v-model="isDark"
            :icon="IconDarkMode"
            label="Dark Mode"
            description="Enable or disable dark mode"
          />
        </MoreMenuSubsection>
      </MoreMenuSection>

      <MoreMenuSection v-if="isDemo || isNotProduction">
        <MoreMenuSectionTitle> Demo </MoreMenuSectionTitle>

        <MoreMenuSubsection>
          <MoreMenuItemAction
            v-if="isDemo"
            :icon="IconUnapproveDevice"
            label="Unapprove device"
            description="Unnaprove device and repair"
            @click="handleUnapproveDeviceClick"
          />
          <MoreMenuItemAction
            v-if="isNotProduction"
            :icon="IconUnapproveDevice"
            label="Change Merchant"
            description="Pair to a different merchant"
            @click="overrideMidDrawerIsOpen = true"
          />
        </MoreMenuSubsection>
      </MoreMenuSection>
    </div>
  </StackPageLayout>

  <OverrideMidDrawer
    :is-open="overrideMidDrawerIsOpen"
    @close="overrideMidDrawerIsOpen = false"
  />
</template>

<script setup lang="ts">
import {
  IconTransactionReceipts,
  IconReceiptFilled as IconHistory,
  IconReports,
  IconDarkMode,
  IconUnapproveDevice,
  IconCardReceipts,
  IconQRCode,
  IconPhone,
} from "@/icons"
import { unapproveDevice } from "@/services/demo/unapproveDevice"
import { partialUpdateSettings } from "@/services/settings/partialUpdateSettings"
import type { TenantState } from "@/composables/useTenant"

const {
  isDemo,
  isCardReceiptsEnabled,
  isTransactionReceiptsEnabled,
  isReceiptQrCodeEnabled,
  accessControlRestrict,
  setTenantState,
  tenant,
} = useTenant()
const { clearTokens } = useAuth()
const router = useRouter()
const {
  isCardReceiptsToggleable,
  isCardholderNotPresentToggleable,
  printingSupported,
} = useDevice()
const { cardholderNotPresentEnabled } =
  useCardholderNotPresent()
const { isDark, isDarkModeAvailable } = useDarkMode()

const handleUnapproveDeviceClick = async () => {
  await unapproveDevice()
  clearTokens()
}

const handleToggleTransactionReceipts = async (
  newValue: boolean
) => {
  try {
    const newState = await partialUpdateSettings({
      isTransactionReceiptsEnabled: newValue,
    })
    setTenantState(newState)
  } catch {
    toast.error("Failed to toggle transaction receipts")
  }
}
const handleToggleQRCodesReceipts = async (
  newValue: boolean
) => {
  try {
    const newState = await partialUpdateSettings({
      isReceiptQrCodeEnabled: newValue,
    })
    setTenantState(newState)
  } catch {
    toast.error("Failed to toggle QR Code on receipts")
  }
}

const handleToggleCardReceipts = async (
  newValue: boolean
) => {
  try {
    const newState = await partialUpdateSettings({
      isCardReceiptsEnabled: newValue,
    })
    setTenantState(newState)
  } catch {
    toast.error("Failed to toggle card receipts")
  }
}

const handleToggleAccessControl = async (
  viewName: "transactions" | "reports",
  newValue: boolean
) => {
  try {
    const newState = await partialUpdateSettings({
      [viewName === "transactions"
        ? "accessControlRestrictTransactions"
        : "accessControlRestrictReports"]: newValue,
    })
    setTenantState(newState)
  } catch {
    toast.error("Failed to toggle access control setting")
  }
}

const updateSetting = async (
  key: keyof TenantState,
  value: TenantState[typeof key]
) => {
  try {
    const newState = await partialUpdateSettings({
      [key]: value,
    })
    setTenantState(newState)
  } catch {
    toast.error("Failed to update setting")
  }
}

const navigateToMoreMenu = () => {
  router.push({ name: "moreMenu" })
}

const isNotProduction =
  import.meta.env.VITE_ENVIRONMENT !== "production"

const overrideMidDrawerIsOpen = ref(false)
</script>
